import { DateTime } from "luxon";
import { type CensusOverride, ChangesSource, type DraftChange, DraftChangeType, type NurseInShift, type WeekData } from "./models";
import { ONLY_DAY_INDEX, rowsOnDate } from "./panelWeeks";
import { toIsoDate } from "./scheduleWeeks";
import { getCensus } from "./utilFunctions";

/** What a draft compares on the drafted day: its scheduled shifts and its census. */
export type DraftDay = {
	date: DateTime;
	shifts: NurseInShift[];
	/** The census the day shows: its override, else its shifts' census. */
	census: number;
	censusOverride: CensusOverride | undefined;
	/** The shift type the day's census override is saved under: its first. */
	censusShiftType: string;
};

/** `date` in `weeks`, by date: a row has no entry on a day its shift type has no staffing for. Undefined when no row has the day. */
export const draftDayOf = (weeks: WeekData[], date: DateTime): DraftDay | undefined => {
	const week = weeks.find((candidate) => rowsOnDate(candidate.weeklyShifts, date).length > 0);
	if (!week) return undefined;
	const rows = rowsOnDate(week.weeklyShifts, date);
	return {
		date,
		shifts: rows.flatMap((row) => row.days[ONLY_DAY_INDEX].nurses),
		census: getCensus({ ...week, weeklyShifts: rows }, ONLY_DAY_INDEX),
		censusOverride: rows[0].days[ONLY_DAY_INDEX].census_override ?? undefined,
		censusShiftType: rows[0].type,
	};
};

/** The census change a draft publishes: a new or changed override is saved, a removed one deleted. */
export const draftCensusChangeOf = (originalDay: DraftDay, activeDay: DraftDay): DraftChange | undefined => {
	const current = activeDay.censusOverride;
	const original = originalDay.censusOverride;
	const date = toIsoDate(activeDay.date);
	const shiftType = activeDay.censusShiftType;
	if (current && (current.override_value !== original?.override_value || current.note !== original?.note)) {
		return {
			id: `${DraftChangeType.UPDATE_CENSUS}_${Date.now()}_${Math.random()}`,
			type: DraftChangeType.UPDATE_CENSUS,
			timestamp: DateTime.now(),
			payload: { censusOverride: current.override_value, censusNote: current.note, date, shiftType, source: ChangesSource.DaySchedule },
		};
	}
	if (!current && original) {
		return {
			id: `${DraftChangeType.DELETE_CENSUS_OVERRIDE}_${Date.now()}_${Math.random()}`,
			type: DraftChangeType.DELETE_CENSUS_OVERRIDE,
			timestamp: DateTime.now(),
			payload: { date, shiftType, source: ChangesSource.DaySchedule },
		};
	}
	return undefined;
};

const DIFF_FIELDS = ["charge", "trainingStatus", "bonus", "location", "notes"] as const;

/** What changed on a shift a draft kept: the fields it compares and its times. Null when nothing did. */
const shiftDiffOf = (original: NurseInShift, current: NurseInShift): Partial<NurseInShift> | null => {
	const diff: Partial<NurseInShift> = {};
	for (const field of DIFF_FIELDS) {
		if (original[field] !== current[field]) Object.assign(diff, { [field]: current[field] });
	}
	if (original.startTime?.toISO() !== current.startTime?.toISO()) diff.startTime = current.startTime;
	if (original.endTime?.toISO() !== current.endTime?.toISO()) diff.endTime = current.endTime;
	return Object.keys(diff).length > 0 ? diff : null;
};

/** The change for a shift the draft added or changed, or undefined when it's unchanged or added and already removed. */
const changeOfCurrentShift = (current: NurseInShift, original: NurseInShift | undefined, date: string): DraftChange | undefined => {
	if (!original) {
		if (current.isPendingToDelete) return undefined;
		if (current.requestId) {
			return {
				id: `${DraftChangeType.APPROVE_ADD_REQUEST}_${Date.now()}_${Math.random()}`,
				type: DraftChangeType.APPROVE_ADD_REQUEST,
				timestamp: DateTime.now(),
				payload: { nurse: current, requestId: current.requestId, date },
			};
		}
		return {
			id: `${DraftChangeType.ADD}_${Date.now()}_${Math.random()}`,
			type: DraftChangeType.ADD,
			timestamp: DateTime.now(),
			payload: { nurse: current, location: current.location, date },
		};
	}
	if (current.isPendingToDelete && !original.isPendingToDelete) {
		return {
			id: `${DraftChangeType.REMOVE}_${current.id}_${Date.now()}`,
			type: DraftChangeType.REMOVE,
			timestamp: DateTime.now(),
			payload: { nurse: current, message: current.message || "", date },
		};
	}
	const diff = shiftDiffOf(original, current);
	return diff
		? {
				id: `${DraftChangeType.UPDATE}_${current.id}_${Date.now()}`,
				type: DraftChangeType.UPDATE,
				timestamp: DateTime.now(),
				payload: { nurse: current, diff, date },
			}
		: undefined;
};

/** The change for a shift the draft took off the day: a removal, or the replaced side of an approved replace request. */
const changeOfGoneShift = (original: NurseInShift, currentShifts: NurseInShift[], date: string): DraftChange => {
	const replacement = original.replacedWith === undefined ? undefined : currentShifts.find((shift) => shift.nurseId === original.replacedWith);
	if (replacement) {
		return {
			id: `${DraftChangeType.APPROVE_REPLACE_REQUEST}_${Date.now()}_${Math.random()}`,
			type: DraftChangeType.APPROVE_REPLACE_REQUEST,
			timestamp: DateTime.now(),
			payload: { nurseToRemove: original, nurse: replacement, requestId: replacement.requestId, date },
		};
	}
	return {
		id: `${DraftChangeType.REMOVE}_${Date.now()}_${Math.random()}`,
		type: DraftChangeType.REMOVE,
		timestamp: DateTime.now(),
		payload: { nurseToRemove: original, message: original.message || "", date },
	};
};

/** The shift changes a draft publishes for one day: `active` against the copy taken when the draft first touched it. Each carries the day's date. */
export const draftShiftChangesOf = (original: DraftDay, active: DraftDay): DraftChange[] => {
	const date = toIsoDate(active.date);
	const kept = active.shifts.flatMap((current) => {
		const change = changeOfCurrentShift(
			current,
			original.shifts.find((shift) => shift.id === current.id),
			date,
		);
		return change ? [change] : [];
	});
	const gone = original.shifts
		.filter((shift) => !active.shifts.some((current) => current.id === shift.id))
		.map((shift) => changeOfGoneShift(shift, active.shifts, date));
	return [...kept, ...gone];
};
